import { Client, cacheExchange, fetchExchange } from 'urql';
import { idempotencyExchange } from './exchanges';
const ACCESS_TOKEN_HEADER = 'X-Access-Token';
const ACCESS_TOKEN_STATUS_HEADER = 'X-Access-Token-Status';
/**
 * Wraps the global fetch to ensure urql always receives a valid JSON response body.
 * Network errors (Failed to fetch / Load failed) are NOT caught here — they propagate
 * naturally so the ErrorBoundary can show NetworkErrorPage.
 *
 * This prevents SyntaxError ("undefined" is not valid JSON) when the API returns
 * a non-JSON response (e.g. 502 HTML page from a gateway/proxy).
 *
 * It is also the only place the raw `Response` is available, so it is where the
 * `X-Access-Token-Status` header is read — urql's `OperationResult` carries no
 * response headers.
 *
 * A request carrying a file has a `FormData` body (urql builds it for the GraphQL
 * multipart spec); it goes to `uploadUrl` when one is given.
 */
const createSafeFetch = (uploadUrl, onAccessTokenStatus) => async (input, init) => {
    const carriesFile = init?.body instanceof FormData;
    const response = await fetch(carriesFile && uploadUrl ? uploadUrl : input, init);
    const accessTokenStatus = response.headers.get(ACCESS_TOKEN_STATUS_HEADER);
    if (accessTokenStatus) {
        onAccessTokenStatus?.(accessTokenStatus);
    }
    const text = await response.text();
    try {
        JSON.parse(text);
    }
    catch {
        return new Response(JSON.stringify({
            errors: [{ message: `Server returned non-JSON response (HTTP ${response.status})` }],
        }), {
            status: response.status,
            statusText: response.statusText,
            headers: new Headers({ 'content-type': 'application/json' }),
        });
    }
    return new Response(text, {
        status: response.status,
        statusText: response.statusText,
        headers: response.headers,
    });
};
export const createGraphQLClient = (config) => {
    const { url, uploadUrl, getToken, getLanguage, getAccessToken, onAccessTokenStatus } = config;
    if (!url) {
        throw new Error('GraphQL URL is missing.');
    }
    return new Client({
        url,
        requestPolicy: 'cache-and-network',
        exchanges: [cacheExchange, idempotencyExchange, fetchExchange],
        fetch: createSafeFetch(uploadUrl, onAccessTokenStatus),
        fetchOptions: () => {
            const token = getToken?.();
            const accessToken = getAccessToken?.();
            const language = getLanguage?.();
            return {
                headers: {
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                    ...(accessToken ? { [ACCESS_TOKEN_HEADER]: accessToken } : {}),
                    ...(language ? { 'Accept-Language': language } : {}),
                },
            };
        },
    });
};
